import { Box, Skeleton, pn, Typography } from '@compass/core';
import { Column } from 'devextreme-react/data-grid';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { DevExpressReport } from 'components';
import { useGenderData } from '../contexts';
import { GenderFilterControls } from './GenderFilterControls';
import { DashboardCard } from '../components/DashboardCard';
import { GenderDistributionDto } from 'generated/openApi';

const GenderTable = (props: { rows: GenderDistributionDto[]; loading: boolean }) => {
    const appTenantName = useSelector((state: RootState) => state.app.tenantName);
    
    if (props.loading) {
        return <Skeleton variant="rounded" height={400} />;
    }
    
    return (
        <DevExpressReport
            dataSource={props.rows}
            fullPageSize
            showColumnChooser={false}
            exportFormats={['pdf', 'excel', 'csv']}
            onExporting={() => ({
                header: 'Gender',
                filename: 'Gender_Distribution',
                tenantName: appTenantName,
            })}
        >
            <Column
                dataField={pn<GenderDistributionDto>('year')}
                caption="School Year"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}-${data.value + 1}`}
            />
            <Column
                dataField={pn<GenderDistributionDto>('genderName')}
                caption="Gender"
                alignment="left"
            />
            <Column
                dataField={pn<GenderDistributionDto>('studentCount')}
                caption="Count"
                alignment="left"
            />
            <Column
                dataField={pn<GenderDistributionDto>('percentage')}
                caption="Percentage"
                alignment="left"
                cellRender={(data: { value: number }) => `${data.value}%`}
            />
        </DevExpressReport>
    );
};

const GenderTableCardContent = () => {
    const { data, loading, error } = useGenderData();
    
    if (error) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <GenderFilterControls />
                <Typography color="error" align="center" sx={{ mt: 4 }}>
                    {error}
                </Typography>
            </Box>
        );
    }
    
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <GenderFilterControls />
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>
                <GenderTable rows={data} loading={loading} />
            </Box>
        </Box>
    );
};

export const GenderTableCard = () => {
    const { data, filter } = useGenderData();

    // Export handler - passed directly as prop to DashboardCard
    const handleExport = () => {
        if (!data || data.length === 0) return;

        const yearsLabel = filter.schoolYears.length > 0
            ? filter.schoolYears.map(y => `${y}-${y + 1}`).join(', ')
            : 'All Years';
        const exportDate = new Date().toLocaleDateString();
        
        // Metadata headers
        const metadata = [
            '# Gender Distribution',
            `# Years: ${yearsLabel}`,
            `# Exported: ${exportDate}`,
            '',
        ];
        
        const headers = 'School Year,Gender,Count,Percentage';
        
        const rows = data.map(item => [
            `${item.year}-${item.year + 1}`,
            `"${item.genderName}"`,
            item.studentCount,
            `${item.percentage}%`
        ]);

        const csvContent = [
            ...metadata,
            headers, 
            ...rows.map(row => row.join(','))
        ].join('\n');

        // Create and trigger download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const yearsFilename = filter.schoolYears.length > 0
            ? filter.schoolYears.join('_')
            : 'AllYears';
        link.setAttribute('download', `Gender_${yearsFilename}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <DashboardCard title="Gender" onExport={handleExport}>
            <GenderTableCardContent />
        </DashboardCard>
    );
};